"use client";

import { useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";

// WebGL scene — must not SSR (needs canvas/WebGL). Loads after the shell
// paints, so the nav + loading state show instantly.
const AnatomyScene = dynamic(() => import("./AnatomyScene"), {
  ssr: false,
  loading: () => (
    <div className="fixed inset-0 flex items-center justify-center bg-[#05080f]">
      <p className="animate-pulse font-[family-name:var(--font-jetbrains-mono)] text-xs tracking-[0.3em] text-cyan-400/60">
        INITIALIZING BLUEPRINT…
      </p>
    </div>
  ),
});

const AnatomyExperience = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const onScrollOffsetRef = useRef<(o: number) => void>(() => {});

  useEffect(() => {
    // Wire the header fade to the scene's scroll offset without re-renders.
    onScrollOffsetRef.current = (o: number) => {
      if (headerRef.current) {
        const opacity = Math.max(0, 1 - o * 14);
        headerRef.current.style.opacity = String(opacity);
        headerRef.current.style.pointerEvents = opacity < 0.01 ? "none" : "auto";
      }
    };
  }, []);

  return (
    <main className="relative h-screen w-full overflow-hidden bg-[#05080f]">
      <AnatomyScene onScrollOffsetRef={onScrollOffsetRef} />

      {/* fixed nav over the canvas */}
      <nav className="pointer-events-none fixed top-0 z-50 flex w-full items-center justify-between px-4 py-3 sm:px-6">
        <Link
          href="/work/bidflow"
          className="pointer-events-auto font-[family-name:var(--font-jetbrains-mono)] text-xs tracking-widest text-cyan-300/70 transition-colors hover:text-cyan-200"
        >
          &larr; BIDFLOW CASE STUDY
        </Link>
        <Link
          href="/"
          className="pointer-events-auto font-[family-name:var(--font-jetbrains-mono)] text-xs tracking-widest text-white/40 transition-colors hover:text-white/80"
        >
          MDG::LAB
        </Link>
      </nav>

      {/* Header overlay — fades as user scrolls */}
      <div
        ref={headerRef}
        className="pointer-events-none fixed left-0 top-[6vh] z-40 w-full text-center transition-none"
        style={{ opacity: 1 }}
      >
        <div className="mx-auto w-[92vw] max-w-2xl">
          <p className="font-[family-name:var(--font-jetbrains-mono)] text-[11px] tracking-[0.3em] text-cyan-400/70">
            ANATOMY OF A SHIPPED APP
          </p>
          <h1 className="mt-2 font-[family-name:var(--font-space-grotesk)] text-6xl font-bold tracking-tight text-white sm:text-8xl">
            BidFlow
          </h1>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-white/55">
            Scroll to descend through a live production AI app — six layers, one
            request, from screen to database.
          </p>
          <div className="mx-auto mt-7 grid max-w-lg grid-cols-2 gap-px overflow-hidden rounded-md border border-cyan-400/20 bg-cyan-400/[0.03] font-[family-name:var(--font-jetbrains-mono)] text-[11px] sm:grid-cols-4">
            {[
              ["SCALE", "Solo"],
              ["DURATION", "~3 mo"],
              ["FILES", "630"],
              ["TS", "~105K"],
            ].map(([k, v]) => (
              <div key={k} className="bg-black/30 px-3 py-3">
                <div className="text-cyan-400/50">{k}</div>
                <div className="mt-1 text-white/80">{v}</div>
              </div>
            ))}
          </div>
          <p className="mt-8 animate-pulse font-[family-name:var(--font-jetbrains-mono)] text-[10px] tracking-[0.3em] text-cyan-300/50">
            ↓ SCROLL TO DESCEND
          </p>
        </div>
      </div>
    </main>
  );
};

export default AnatomyExperience;
